import type { AxiosInstance } from "axios";
import { useClient } from "../state/client";
import { useMutation, useQuery } from "@tanstack/solid-query";
import { initialized } from "../utils/initializedUtil";
import type { MoreAppError } from "../MoreAppError";
import type { LanguageCode } from "../state/i18n";
import type { InviteStatus, InviteSummary } from "../state/invite";
import { toInviteSummary } from "../utils/mappers/invite";
import { doNotRetryOnUnrecoverableError } from "../utils/apiUtil";
import type CustomerId from "../state/CustomerId";
import { MINIMUM_PAGE_LOADING_DELAY } from "../constants";
import pMinDelay from "p-min-delay";
import type { UserDto } from "./UserService.ts";
import { toUser } from "../utils/mappers/user.ts";
import type { User } from "../state/user.tsx";

export type UserAccountInformationDto = {
  firstName: string;
  lastName: string;
  language?: LanguageCode;
  country?: string;
  receiveNewsLetter: boolean;
  timeZone?: string;
};

export type InviteSummaryDto = {
  id: string;
  customerId: CustomerId;
  customerName: string;
  emailAddress: string;
  settings: UserAccountInformationDto;
  status: InviteStatus;
};

export type InviteOnboarding = { settings: UserAccountInformationDto; password: string };

let client: AxiosInstance | null = null;

const init = (): void => {
  client = useClient();
};

const getClient = () => initialized(client, "client");

const InviteService = {
  init,
  getInvite: (token: string) =>
    useQuery<InviteSummary, MoreAppError>(() => ({
      queryKey: ["invites", token],
      queryFn: async () => {
        const { data } = await pMinDelay(
          getClient().get<InviteSummaryDto>(`api/v2/invites/${token}`),
          MINIMUM_PAGE_LOADING_DELAY,
          { delayRejection: false },
        );
        return toInviteSummary(data);
      },
      retry: doNotRetryOnUnrecoverableError,
      throwOnError: true,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    })),
  acceptInvite: () =>
    useMutation<User, MoreAppError, { token: string; inviteOnboarding?: InviteOnboarding }>(() => ({
      mutationFn: async ({ token, inviteOnboarding }) => {
        const { data } = await getClient().post<UserDto>(`api/v2/invites/${token}`, inviteOnboarding);
        return toUser(data);
      },
    })),
};

export default InviteService;
